import { FileAPIService } from "./services/fileAPIService";
import { LocalStorageService } from "./services/localStorageService";
import { UoMService } from "./services/uomService";
import { AuthenticationService } from "./services/authenticationService";
import {
  PurchaseOrderAPIService,
  PurchaseOrderDashService,
  PurchaseOrderService,
  TransitPurchaseOrderAPIService,
  TransitPurchaseOrderDashService,
  TransitPurchaseOrderService,
  SubPurchaseOrderAPIService,
  SubPurchaseOrderService
} from "./services/purchaseOrders";
import { TransferOrderAPIService, TransferOrderDashService, TransferOrderService } from "./services/transferOrders";
import {
  ActiveAntsPurchaseOrderAPIService,
  ActiveAntsPurchaseOrderDashService,
  ActiveAntsPurchaseOrderService
} from "./services/activeAntsPurchaseOrders";
import {
  ActiveAntsWarehouseCheckInAPIService,
  ActiveAntsWarehouseCheckInService
} from "./services/activeAntsWarehouseCheckIns";
import {
  ActiveAntsReceivingDiscrepancyAPIService,
  ActiveAntsReceivingDiscrepancyService
} from "./services/activeAntsReceivingDiscrepancies";
import {
  ReceivingDiscrepancyAPIService,
  ReceivingDiscrepancyService
} from "./services/purchaseOrders/receivingDiscrepancies";
import { UserAPIService, UserDashService, UserService } from "./services/users";
import { UserAccountAPIService, UserAccountService } from "./services/users/accounts";
import { UserEmailAPIService, UserEmailService } from "./services/users/emails";
import { UserFeedbackAPIService } from "./services/users/userFeedbackAPIService";
import { VendorAPIService, VendorDashService, VendorService } from "./services/vendors";
import {
  WarehouseCheckInAPIService,
  WarehouseCheckInDashService,
  WarehouseCheckInService
} from "./services/warehouseCheckIns";
import { UnknownArrivalAPIService, UnknownArrivalService } from "./services/warehouseCheckIns/unknownArrivals";
import { ClaimAPIService, ClaimService, ClaimDashService } from "./services/claims";
import {
  HmrcExchangeRateAPIService,
  HmrcExchangeRateService,
  InvoiceExportAPIService,
  InvoiceExportService
} from "./services/finance";
import { EuRelabelCheckInAPIService, EuRelabelCheckInService } from "./services/euRelabelCheckIns";
import { WarehouseRoutingCheckInAPIService, WarehouseRoutingCheckInService } from "./services/warehouseRoutingCheckIns";
import { RelabelCaseAPIService, RelabelCaseService } from "./services/relabelCases";
import { PalletAPIService, PalletService, PalletLocationAPIService, PalletLocationService } from "./services/pallets";
import { ShipmentAPIService, ShipmentService } from "./services/shipments";
import { BrandAPIService, BrandService } from "./services/brands";

const instances = new Map<string, object>();

export function getInstance<T extends object>(name: string, factory: () => T): T {
  if (!instances.has(name)) {
    const instance = factory();
    instances.set(name, instance);
  }
  return instances.get(name) as T;
}

export function getInstanceOfFileAPIService(): FileAPIService {
  return getInstance("FileAPIService", () => new FileAPIService());
}

export function getInstanceOfLocalStorageService(): LocalStorageService {
  return getInstance("LocalStorageService", () => new LocalStorageService());
}

export function getInstanceOfUoMService(): UoMService {
  return getInstance("UoMService", () => new UoMService(getInstanceOfLocalStorageService()));
}

export function getInstanceOfAuthenticationService(): AuthenticationService {
  return getInstance("AuthenticationService", () => new AuthenticationService());
}

export function getInstanceOfPurchaseOrderService(): PurchaseOrderService {
  return getInstance(
    "PurchaseOrderService",
    () =>
      new PurchaseOrderService(
        new PurchaseOrderAPIService(getInstanceOfAuthenticationService()),
        getInstanceOfFileAPIService()
      )
  );
}

export function getInstanceOfPurchaseOrderDashService(): PurchaseOrderDashService {
  return getInstance(
    "PurchaseOrderDashService",
    () => new PurchaseOrderDashService(getInstanceOfLocalStorageService())
  );
}

export function getInstanceOfTransitPurchaseOrderService(): TransitPurchaseOrderService {
  return getInstance(
    "TransitPurchaseOrderService",
    () =>
      new TransitPurchaseOrderService(
        new TransitPurchaseOrderAPIService(getInstanceOfAuthenticationService()),
        getInstanceOfFileAPIService()
      )
  );
}

export function getInstanceOfTransitPurchaseOrderDashService(): TransitPurchaseOrderDashService {
  return getInstance(
    "TransitPurchaseOrderDashService",
    () => new TransitPurchaseOrderDashService(getInstanceOfLocalStorageService())
  );
}

export function getInstanceOfSubPurchaseOrderService(): SubPurchaseOrderService {
  return getInstance(
    "SubPurchaseOrderService",
    () => new SubPurchaseOrderService(new SubPurchaseOrderAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfReceivingDiscrepancyService(): ReceivingDiscrepancyService {
  return getInstance(
    "ReceivingDiscrepancyService",
    () => new ReceivingDiscrepancyService(new ReceivingDiscrepancyAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfUserService(): UserService {
  return getInstance("UserService", () => new UserService(new UserAPIService(getInstanceOfAuthenticationService())));
}

export function getInstanceOfUserDashService(): UserDashService {
  return getInstance("UserDashService", () => new UserDashService(getInstanceOfLocalStorageService()));
}

export function getInstanceOfUserAccountService(): UserAccountService {
  return getInstance(
    "UserAccountService",
    () => new UserAccountService(new UserAccountAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfUserEmailService(): UserEmailService {
  return getInstance(
    "UserEmailService",
    () => new UserEmailService(new UserEmailAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfUserFeedbackAPIService(): UserFeedbackAPIService {
  return getInstance("UserFeedbackAPIService", () => new UserFeedbackAPIService(getInstanceOfAuthenticationService()));
}

export function getInstanceOfVendorService(): VendorService {
  return getInstance(
    "VendorService",
    () => new VendorService(new VendorAPIService(getInstanceOfAuthenticationService()), getInstanceOfFileAPIService())
  );
}

export function getInstanceOfVendorDashService(): VendorDashService {
  return getInstance("VendorDashService", () => new VendorDashService(getInstanceOfLocalStorageService()));
}

export function getInstanceOfWarehouseCheckInService(): WarehouseCheckInService {
  return getInstance(
    "WarehouseCheckInService",
    () =>
      new WarehouseCheckInService(
        new WarehouseCheckInAPIService(getInstanceOfAuthenticationService()),
        getInstanceOfFileAPIService()
      )
  );
}

export function getInstanceOfWarehouseCheckInDashService(): WarehouseCheckInDashService {
  return getInstance(
    "WarehouseCheckInDashService",
    () => new WarehouseCheckInDashService(getInstanceOfLocalStorageService())
  );
}

export function getInstanceOfUnknownArrivalService(): UnknownArrivalService {
  return getInstance(
    "UnknownArrivalService",
    () => new UnknownArrivalService(new UnknownArrivalAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfClaimService(): ClaimService {
  return getInstance(
    "ClaimService",
    () => new ClaimService(new ClaimAPIService(getInstanceOfAuthenticationService()), getInstanceOfFileAPIService())
  );
}

export function getInstanceOfClaimDashService(): ClaimDashService {
  return getInstance("ClaimDashService", () => new ClaimDashService(getInstanceOfLocalStorageService()));
}

export function getInstanceOfHmrcExchangeRateService(): HmrcExchangeRateService {
  return getInstance(
    "HmrcExchangeRateService",
    () => new HmrcExchangeRateService(new HmrcExchangeRateAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfInvoiceExportService(): InvoiceExportService {
  return getInstance(
    "InvoiceExportService",
    () =>
      new InvoiceExportService(
        new InvoiceExportAPIService(getInstanceOfAuthenticationService()),
        getInstanceOfFileAPIService()
      )
  );
}

export function getInstanceOfEuRelabelCheckInService(): EuRelabelCheckInService {
  return getInstance(
    "EuRelabelCheckInService",
    () => new EuRelabelCheckInService(new EuRelabelCheckInAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfWarehouseRoutingCheckInService(): WarehouseRoutingCheckInService {
  return getInstance(
    "WarehouseRoutingCheckInService",
    () =>
      new WarehouseRoutingCheckInService(new WarehouseRoutingCheckInAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfRelabelCaseService(): RelabelCaseService {
  return getInstance(
    "RelabelCaseService",
    () => new RelabelCaseService(new RelabelCaseAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfPalletService(): PalletService {
  return getInstance(
    "PalletService",
    () => new PalletService(new PalletAPIService(getInstanceOfAuthenticationService()), getInstanceOfFileAPIService())
  );
}

export function getInstanceOfPalletLocationService(): PalletLocationService {
  return getInstance(
    "PalletLocationService",
    () => new PalletLocationService(new PalletLocationAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfShipmentService(): ShipmentService {
  return getInstance(
    "ShipmentService",
    () =>
      new ShipmentService(new ShipmentAPIService(getInstanceOfAuthenticationService()), getInstanceOfFileAPIService())
  );
}

export function getInstanceOfBrandService(): BrandService {
  return getInstance("BrandService", () => new BrandService(new BrandAPIService(getInstanceOfAuthenticationService())));
}

export function getInstanceOfTransferOrderService(): TransferOrderService {
  return getInstance(
    "TransferOrderService",
    () => new TransferOrderService(new TransferOrderAPIService(getInstanceOfAuthenticationService()))
  );
}

export function getInstanceOfTransferOrderDashService(): TransferOrderDashService {
  return getInstance(
    "TransferOrderDashService",
    () => new TransferOrderDashService(getInstanceOfLocalStorageService())
  );
}

export function getInstanceOfActiveAntsPurchaseOrderService(): ActiveAntsPurchaseOrderService {
  return getInstance(
    "ActiveAntsPurchaseOrderService",
    () =>
      new ActiveAntsPurchaseOrderService(
        new ActiveAntsPurchaseOrderAPIService(getInstanceOfAuthenticationService()),
        getInstanceOfFileAPIService()
      )
  );
}

export function getInstanceOfActiveAntsPurchaseOrderDashService(): ActiveAntsPurchaseOrderDashService {
  return getInstance(
    "ActiveAntsPurchaseOrderDashService",
    () => new ActiveAntsPurchaseOrderDashService(getInstanceOfLocalStorageService())
  );
}

export function getInstanceOfActiveAntsWarehouseCheckInService(): ActiveAntsWarehouseCheckInService {
  return getInstance(
    "ActiveAntsWarehouseCheckInService",
    () =>
      new ActiveAntsWarehouseCheckInService(
        new ActiveAntsWarehouseCheckInAPIService(getInstanceOfAuthenticationService()),
        getInstanceOfFileAPIService()
      )
  );
}

export function getInstanceOfActiveAntsReceivingDiscrepancyService(): ActiveAntsReceivingDiscrepancyService {
  return getInstance(
    "ActiveAntsReceivingDiscrepancyService",
    () =>
      new ActiveAntsReceivingDiscrepancyService(
        new ActiveAntsReceivingDiscrepancyAPIService(getInstanceOfAuthenticationService())
      )
  );
}
